import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, MessageSquarePlus, Box, Sparkles, RefreshCw, TerminalSquare } from 'lucide-react';
import { useStore } from '../../store/useStore';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface ChatAppProps {
  projectId?: string;
  title?: string;
}

export function ChatApp({ projectId, title }: ChatAppProps) {
  const currentProject = useStore((s) => s.currentProject);
  const addNotification = useStore((s) => s.addNotification);
  const openApp = useStore((s) => s.openApp);

  const activeProject = projectId
    ? (currentProject?.id === projectId ? currentProject : { id: projectId, name: title || 'Project' })
    : currentProject;

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [mode, setMode] = useState<'chat' | 'terminal'>('chat');
  const scrollRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
    };
  }, []);

  const checkConnection = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/chat/status`, { credentials: 'include' });
      const data = await res.json();
      setConnected(!!data.configured);
    } catch {
      setConnected(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, []);

  const promptForProject = () => {
    if (!currentProject) {
      addNotification('Info', 'Open a project first so the assistant can access it', 'info');
      openApp('projects');
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    promptForProject();
    setInput('');
    const userMsg: Message = { role: 'user', content: text, timestamp: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setStreaming(true);

    try {
      // Try SSE streaming; fall back to normal POST
      if (eventSourceRef.current) eventSourceRef.current.close();
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          projectId: currentProject?.id,
          history: messages.slice(-10).map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || 'Request failed');
      }
      if (!res.body) throw new Error('No response body');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = '';
      setMessages((prev) => [...prev, { role: 'assistant', content: '', timestamp: Date.now() }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        assistantText += chunk;
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === 'assistant') {
            next[next.length - 1] = { ...last, content: assistantText };
          }
          return next;
        });
      }
    } catch (err: any) {
      // Fall back to a helpful terminal-only response
      const fallback = [
        'The AI chat backend is not configured yet.',
        '',
        'You can still use AI coding tools directly in the terminal.',
        activeProject
          ? `Run \`opencode\` or \`freebuff\` in the project at /workspace/${activeProject.name}`
          : 'Open a project, then run `opencode` from the terminal.',
        '',
        'To enable the chat panel, set the following backend env vars:',
        '  CHAT_API_KEY=... and CHAT_API_BASE=... (default: OpenAI-compatible API)',
      ].join('\n');
      setMessages((prev) => [...prev, { role: 'assistant', content: fallback, timestamp: Date.now() }]);
      setTimeout(() => {
        if (activeProject) openApp('terminal', { projectName: activeProject.name, projectId: activeProject.id });
      }, 300);
    } finally {
      setStreaming(false);
    }
  };

  const runInTerminal = (command: string) => {
    if (!activeProject) {
      addNotification('Info', 'Open a project first', 'info');
      openApp('projects');
      return;
    }
    openApp('terminal', { projectName: activeProject.name, projectId: activeProject.id, command });
    setInput('');
  };

  const suggestions = [
    'Help me create a new React component',
    'Review my code for bugs',
    'Explain what this project does',
    'Set up a dev server for this project',
  ];

  return (
    <div className="h-full flex flex-col bg-desktop-surface2">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-desktop-border bg-desktop-surface shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-desktop-text">AI Assistant</p>
            <div className="flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-desktop-success' : 'bg-desktop-warning'}`} />
              <span className="text-[10px] text-desktop-text/50">
                {connected ? 'Connected' : 'Fallback mode'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMode(mode === 'chat' ? 'terminal' : 'chat')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg transition-colors ${
              mode === 'terminal'
                ? 'bg-desktop-accent/20 text-desktop-accent'
                : 'text-desktop-text/60 hover:text-desktop-text'
            }`}
            title="Toggle between chat and terminal command mode"
          >
            {mode === 'chat' ? (
              <>
                <TerminalSquare className="w-3.5 h-3.5" /> Commands
              </>
            ) : (
              <>
                <MessageSquarePlus className="w-3.5 h-3.5" /> Chat
              </>
            )}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-desktop-text">How can I help you code?</p>
              <p className="text-xs text-desktop-text/50 mt-1">
                {activeProject
                  ? `Working in project "${activeProject.name}"`
                  : 'Open a project to give the assistant access to your code'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 justify-center max-w-sm">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  disabled={streaming}
                  className="px-3 py-1.5 text-xs rounded-full border border-desktop-border bg-desktop-surface hover:bg-desktop-hover text-desktop-text/70 transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'assistant' && (
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shrink-0 mt-1">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
            )}
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-desktop-accent/20 border border-desktop-accent/30 text-desktop-text'
                  : 'bg-desktop-surface border border-desktop-border text-desktop-text'
              }`}
            >
              {m.content + (streaming && i === messages.length - 1 && m.role === 'assistant' ? '▊' : '')}
            </div>
            {m.role === 'user' && (
              <div className="w-6 h-6 rounded-md bg-desktop-surface border border-desktop-border flex items-center justify-center shrink-0 mt-1">
                <User className="w-3.5 h-3.5 text-desktop-text/70" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Terminal command quick access */}
      {mode === 'terminal' && messages.length > 0 && (
        <div className="px-3 py-1.5 border-t border-desktop-border bg-desktop-surface shrink-0">
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => runInTerminal('opencode')} className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md bg-desktop-accent/15 text-desktop-accent hover:bg-desktop-accent/25 transition-colors">
              <TerminalSquare className="w-3 h-3" /> Run opencode
            </button>
            <button onClick={() => runInTerminal('freebuff')} className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md bg-desktop-accent/15 text-desktop-accent hover:bg-desktop-accent/25 transition-colors">
              <TerminalSquare className="w-3 h-3" /> Run freebuff
            </button>
            <button onClick={() => runInTerminal('ls -la')} className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md bg-desktop-surface2 hover:bg-desktop-hover text-desktop-text/70 transition-colors">
              <Box className="w-3 h-3" /> ls -la
            </button>
            <button onClick={() => runInTerminal('node --version && npm --version')} className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md bg-desktop-surface2 hover:bg-desktop-hover text-desktop-text/70 transition-colors">
              <Box className="w-3 h-3" /> Versions
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-2 border-t border-desktop-border bg-desktop-surface shrink-0">
        <div className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={mode === 'terminal' ? 'Describe what you want to do...' : 'Ask about your code...'}
            disabled={streaming}
            className="flex-1 px-3 py-2 bg-desktop-surface2 border border-desktop-border rounded-lg text-sm text-desktop-text placeholder:text-desktop-text/40 focus:outline-none focus:border-desktop-accent disabled:opacity-50"
          />
          <button
            onClick={sendMessage}
            disabled={streaming || !input.trim()}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-desktop-accent text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {streaming ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[10px] text-desktop-text/40 mt-1.5">
          {activeProject
            ? `Workspace: /workspace/${activeProject.name}`
            : 'Tip: open a project to give the assistant file access'}
        </p>
      </div>
    </div>
  );
}